import React, { useEffect, useState } from 'react';
import { Input } from '../components/UI';
import AuthBackdrop from '../components/AuthBackdrop';
import * as api from '../api';

// Kept in step with _WEAK_PASSWORD_STEMS in backend/app/core/security.py.
// Compared against the password with trailing digits and punctuation stripped,
// so "Welcome123!" is caught by the entry "welcome".
const WEAK_STEMS = new Set([
  'password', 'passw0rd', 'welcome', 'changeme', 'letmein', 'admin',
  'administrator', 'qwerty', 'abc', 'temp', 'temporary', 'default',
  'secret', 'iloveyou', 'rifitel', 'projectflow', 'test', 'user', 'login',
]);

/**
 * Shown after signing in with an administrator-issued temporary password.
 *
 * There is deliberately no way past this screen other than setting a new
 * password or signing out. The password the user just typed was chosen by
 * somebody else and passed to them over the phone — leaving it in place is
 * the thing the whole temporary-password flow exists to prevent.
 *
 * `currentPassword` is carried over from the sign-in that just happened so
 * the user is not asked to type it a second time; the change endpoint still
 * verifies it server-side.
 */
export default function ForcePasswordChangePage({ user, currentPassword, onDone, onLogout }) {
  const [pw, setPw] = useState('');
  const [confirm, setConfirm] = useState('');
  const [current, setCurrent] = useState(currentPassword || '');
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState('');

  // This screen is reached while signed in, so the document is carrying the
  // user's own theme — light, for most people. That matters beyond looks:
  // the Input component's focus/blur handlers write `var(--bg-base)` straight
  // onto the element, which under a light theme repaints the field pale and
  // makes the white password text invisible. Forcing dark for the lifetime of
  // this page keeps it consistent with the sign-in screen it follows and
  // keeps every token resolving to something readable. The previous value is
  // restored on unmount so the rest of the app is unaffected.
  useEffect(() => {
    const root = document.documentElement;
    const previous = root.getAttribute('data-theme');
    root.setAttribute('data-theme', 'dark');
    return () => {
      if (previous) root.setAttribute('data-theme', previous);
      else root.removeAttribute('data-theme');
    };
  }, []);

  // If the sign-in password did not survive (a reload lands here straight from
  // a stored token), fall back to asking for it.
  const needsCurrent = !currentPassword;

  const rules = [
    ['At least 8 characters', pw.length >= 8],
    ['Contains an uppercase letter', /[A-Z]/.test(pw)],
    ['Contains a lowercase letter', /[a-z]/.test(pw)],
    ['Contains a number', /\d/.test(pw)],
  ];

  const allRulesMet = rules.every(([, met]) => met);
  const matches = pw.length > 0 && pw === confirm;
  // Enforced by the server too, but kept out of the checklist — it is not a
  // strength rule, and it only becomes relevant once they actually reuse it.
  const reusesTemp = pw.length > 0 && pw === current;
  // Mirrors the server's blocklist. Without it "Welcome123" ticks all four
  // rules green and is then rejected on submit, which reads as a broken form.
  const tooCommon = pw.length > 0 && WEAK_STEMS.has(
    pw.toLowerCase().replace(/[0-9!@#$%^&*._-]+$/, '')
  );

  const canSubmit = allRulesMet && matches && !reusesTemp && !tooCommon
    && (!needsCurrent || current.length > 0);

  const submit = async () => {
    if (!canSubmit || loading) return;
    setLoading(true); setMsg('');
    try {
      await api.changePassword(current, pw);
      onDone();
    } catch (e) {
      setMsg(e.message || 'Could not change your password.');
    }
    setLoading(false);
  };

  const isDark = true;
  const field = {
    padding: '12px 14px', fontSize: 14, border: '1px solid #6490f6ff',
    background: '#1e1e24', color: 'white', opacity: loading ? 0.6 : 1,
  };

  return (
    <div style={{
      minHeight: '100vh', boxSizing: 'border-box', display: 'flex', padding: 20,
      background: '#0a0a0f', position: 'relative', overflowY: 'auto', overflowX: 'hidden',
    }}>
      <AuthBackdrop isDark={isDark} />

      {/* Same 560px column as the sign-in screen, so the box does not change
          size between the two steps. */}
      <div style={{ position: 'relative', zIndex: 1, width: '100%', maxWidth: 560, margin: 'auto', padding: '40px 0' }}>

        <div style={{ textAlign: 'center', marginBottom: 36, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <div className="login-logo-outer" style={{
            borderRadius: 24,
            background: 'rgba(255,255,255,0.05)',
            backdropFilter: 'blur(12px)', border: '1px solid rgba(255,255,255,0.08)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: '0 16px 40px rgba(0,0,0,0.4), 0 0 60px rgba(153,27,27,0.15)',
          }}>
            <img className="login-logo-inner" src="/rnl-logo.png" alt="ProjectFlow Logo" style={{ borderRadius: 16 }} />
          </div>
          {/* Explicit white rather than var(--text-primary): this heading sits
              on the dark backdrop regardless of theme. */}
          <h1 className="login-title" style={{
            fontWeight: 800, letterSpacing: '-0.04em', color: '#ffffff',
            textShadow: '0 2px 20px rgba(0,0,0,0.5)', margin: 0, textAlign: 'center',
          }}>
            Set your new password
          </h1>
        </div>

        <div className="login-box-padding" style={{
          background: 'rgba(18, 18, 24, 0.65)',
          backdropFilter: 'blur(28px)', WebkitBackdropFilter: 'blur(28px)',
          width: '100%',
          border: '4px solid #9d97abff',
          borderRadius: 24,
          boxShadow: '0 24px 60px rgba(0,0,0,0.5), 0 0 80px rgba(153,27,27,0.15),'
            + ' 0 0 0 1px rgba(255,255,255,0.04), inset 0 1px 0 rgba(255,255,255,0.06)',
        }}>
          {user?.email && (
            <div style={{
              fontSize: 13, color: 'rgba(255,255,255,0.6)', textAlign: 'center',
              marginBottom: 22, padding: '9px 12px', borderRadius: 10,
              background: 'rgba(100,144,246,0.08)', border: '1px solid rgba(100,144,246,0.2)',
            }}>
              Signed in as <strong style={{ color: '#6490f6ff' }}>{user.email}</strong>
            </div>
          )}

          <div className="login-input-gap" style={{ display: 'flex', flexDirection: 'column' }}>
            {needsCurrent && (
              <Input label="Temporary password" type="password" value={current} onChange={setCurrent}
                placeholder="The password your administrator gave you" required disabled={loading} style={field} />
            )}
            <Input label="New password" type="password" value={pw} onChange={setPw}
              placeholder="••••••••••••" required disabled={loading} style={field} />
            <Input label="Confirm new password" type="password" value={confirm} onChange={setConfirm}
              placeholder="••••••••••••" required disabled={loading} style={field} />

            {/* Live rules rather than a rejection after submitting — the server
                enforces the same list, this just saves the round trip. */}
            {/* Two columns, but auto-fit so the rules drop to one on a narrow
                screen rather than wrapping their labels mid-word. */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))',
              gap: '7px 16px',
            }}>
              {rules.map(([label, met]) => (
                <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 9, fontSize: 12.5, minWidth: 0 }}>
                  <span style={{
                    width: 16, height: 16, borderRadius: '50%', flexShrink: 0,
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 10, fontWeight: 900, lineHeight: 1,
                    background: met ? 'rgba(34,197,94,0.2)' : 'rgba(255,255,255,0.06)',
                    color: met ? '#4ade80' : 'rgba(255,255,255,0.3)',
                    border: `1px solid ${met ? 'rgba(34,197,94,0.45)' : 'rgba(255,255,255,0.14)'}`,
                    transition: 'background 0.15s ease, border-color 0.15s ease',
                  }}>{met ? '✓' : ''}</span>
                  <span style={{ color: met ? 'rgba(255,255,255,0.82)' : 'rgba(255,255,255,0.45)' }}>{label}</span>
                </div>
              ))}
            </div>

            {confirm.length > 0 && !matches && (
              <div style={{ fontSize: 12.5, color: '#f87171' }}>
                The two passwords do not match.
              </div>
            )}
            {reusesTemp && (
              <div style={{ fontSize: 12.5, color: '#fbbf24' }}>
                Choose something other than the temporary password you were given.
              </div>
            )}
            {tooCommon && (
              <div style={{ fontSize: 12.5, color: '#fbbf24' }}>
                That password is too easy to guess — pick something less common.
              </div>
            )}

            {msg && (
              <div style={{
                padding: '10px 14px', borderRadius: 8, fontSize: 14,
                background: 'var(--red-dim)', color: 'var(--red)',
                border: '1px solid rgba(239,68,68,0.3)', lineHeight: 1.5,
              }}>
                {msg}
              </div>
            )}

            <button
              onClick={submit}
              disabled={!canSubmit || loading}
              style={{
                width: '100%', marginTop: 8, padding: '12px 0', fontSize: 15, fontWeight: 'bold',
                background: '#6490f6ff', color: '#060505ff', border: 'none', borderRadius: 11,
                cursor: (!canSubmit || loading) ? 'not-allowed' : 'pointer',
                opacity: (!canSubmit || loading) ? 0.4 : 1, transition: 'all 0.2s ease',
                boxShadow: 'none', fontFamily: 'inherit',
              }}
              onMouseEnter={e => { if (canSubmit && !loading) { e.currentTarget.style.boxShadow = '0 0 18px #6490f6ff'; e.currentTarget.style.filter = 'brightness(1.15)'; e.currentTarget.style.transform = 'translateY(-1px)'; } }}
              onMouseLeave={e => { if (canSubmit && !loading) { e.currentTarget.style.boxShadow = 'none'; e.currentTarget.style.filter = 'none'; e.currentTarget.style.transform = 'none'; } }}
            >
              {loading ? 'Saving…' : 'Set Password & Continue'}
            </button>

            <div style={{ textAlign: 'center', marginTop: 2 }}>
              <button onClick={onLogout} disabled={loading}
                style={{
                  background: 'none', border: 'none', color: 'rgba(255,255,255,0.45)',
                  fontSize: 13, cursor: 'pointer', textDecoration: 'underline', fontFamily: 'inherit',
                }}>
                Sign out instead
              </button>
            </div>
          </div>
        </div>

        <div style={{ textAlign: 'center', marginTop: 24, fontSize: 11, color: 'white', letterSpacing: '0.05em' }}>
          © {new Date().getFullYear()} Rifitel Nigeria · All rights reserved
        </div>
      </div>
    </div>
  );
}
